import { apiClient } from "@/lib/api";
import { fetchAllPages } from "@/lib/fetchAllPages";
import type {
  ReaderCreate,
  ReaderInventory,
  PaymentReader,
} from "@/types";

/** Wire shape from `/readers` before normalizing the assigned-device invariant. */
type ReaderInventoryWire = PaymentReader & {
  assigned_device_id?: string | null;
  assigned_device_display_name?: string | null;
  assigned_kiosk_id?: string | null;
};

function normalizeReaderInventory(wire: ReaderInventoryWire): ReaderInventory {
  const {
    assigned_device_id,
    assigned_device_display_name,
    assigned_kiosk_id,
    ...reader
  } = wire;
  const id = assigned_device_id ?? assigned_kiosk_id ?? null;
  if (id == null) {
    return { ...reader, assigned_device: null };
  }
  return {
    ...reader,
    assigned_device: {
      id,
      display_name: assigned_device_display_name ?? id,
    },
  };
}

/** Backend cap: GET /readers `limit` le=200. */
const READERS_PAGE_SIZE = 200;

type ReaderListFilters = {
  serial_number?: string;
  status?: string;
  region_id?: string;
  available_only?: boolean;
};

export const readerKeys = {
  all: ["readers"] as const,
  lists: () => [...readerKeys.all, "list"] as const,
  list: (filters?: ReaderListFilters) =>
    [...readerKeys.lists(), filters ?? {}] as const,
  details: () => [...readerKeys.all, "detail"] as const,
  detail: (id: string) => [...readerKeys.details(), id] as const,
};

export const readerService = {
  getAll: (filters?: ReaderListFilters): Promise<ReaderInventory[]> =>
    fetchAllPages(async (page) => {
      const response = await apiClient.get<ReaderInventoryWire[]>("/readers", {
        params: { ...filters, ...page },
      });
      return response.data.map(normalizeReaderInventory);
    }, READERS_PAGE_SIZE),

  getById: async (readerId: string): Promise<ReaderInventory> => {
    const response = await apiClient.get<ReaderInventoryWire>(
      `/readers/${readerId}`,
    );
    return normalizeReaderInventory(response.data);
  },

  create: async (data: ReaderCreate): Promise<PaymentReader> => {
    const response = await apiClient.post<PaymentReader>("/readers", data);
    return response.data;
  },

  update: async (
    readerId: string,
    data: { serial_number?: string; region_id?: string },
  ): Promise<PaymentReader> => {
    const response = await apiClient.put<PaymentReader>(
      `/readers/${readerId}`,
      data,
    );
    return response.data;
  },

  delete: async (readerId: string): Promise<void> => {
    await apiClient.delete(`/readers/${readerId}`);
  },

  pairSumup: async (
    readerId: string,
    data: { pairing_code: string },
  ): Promise<PaymentReader> => {
    const response = await apiClient.post<PaymentReader>(
      `/readers/${readerId}/sumup/pair`,
      data,
    );
    return response.data;
  },

  unpairSumup: async (
    readerId: string,
    deleteOnSumup = true,
  ): Promise<PaymentReader> => {
    const response = await apiClient.delete<PaymentReader>(
      `/readers/${readerId}/sumup`,
      { params: { delete_on_sumup: deleteOnSumup } },
    );
    return response.data;
  },

  refreshAllDeviceStatuses: async (): Promise<{
    updated: number;
    failed: number;
  }> => {
    const response = await apiClient.post<{ updated: number; failed: number }>(
      `/readers/sumup/status/refresh`,
    );
    return response.data;
  },
};
